import { Skeleton } from '@/components/ui/Skeleton.tsx';

/**
 * Kawałki wspólne dla szkieletów kroków: nagłówek kroku i tabela zmian, która
 * stoi zarówno w kroku W1, jak i w karcie klienta w kroku W3.
 *
 * Marginesy idą za tym samym progiem co treść (`changeSummaryTableLayout.ts`),
 * więc szkielet zwęża się razem z kartą i przejście z ładowania do danych
 * niczego nie przesuwa. Obie części zakładają, że stoją w `@container` — na
 * kroku daje go karta.
 */

const StepHeaderSkeleton = () => (
  <div className="@min-[700px]:px-7 border-b border-neutral-100 px-4 pb-5 pt-[22px]">
    <div className="flex items-center gap-2.5">
      <Skeleton className="h-6 w-28 rounded-full" />
      <Skeleton className="h-3 w-40" />
    </div>
    <Skeleton className="mt-2.5 h-6 w-64" />
    <Skeleton className="mt-2.5 h-4 w-[520px] max-w-full" />
  </div>
);

/**
 * Wiersz zmiany. Wąsko wiersz ma trzy linie (nazwa, nowa wartość, kwota),
 * szeroko jedną w czterech kolumnach — szkielet musi mieć tę samą wysokość co
 * treść, inaczej lista podskoczy w chwili wejścia danych.
 */
const SummaryRowSkeleton = () => (
  <div className="@min-[700px]:px-7 border-b border-neutral-100 px-4">
    <div className="@min-[700px]:hidden flex flex-col gap-1.5 py-3 pl-[38px]">
      <Skeleton className="h-3 w-28" />
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-3 w-20" />
    </div>
    <div className="@max-[700px]:hidden flex items-center gap-5 py-3.5">
      <Skeleton className="ml-[38px] h-4 w-40" />
      <Skeleton className="ml-auto h-4 w-28" />
      <Skeleton className="h-4 w-28" />
      <Skeleton className="h-4 w-20" />
    </div>
  </div>
);

/** Nagłówek kolumn, pasek osoby i jej pozycje — tyle, ile widać przed danymi. */
const SummaryTableSkeleton = ({ rowCount = 4 }: { rowCount?: number }) => (
  <>
    <div className="@max-[440px]:hidden @min-[700px]:px-7 flex items-center gap-5 border-b border-neutral-100 px-4 pb-2.5 pt-4">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="@max-[700px]:hidden ml-auto h-3 w-16" />
      <Skeleton className="@max-[700px]:hidden h-3 w-20" />
      <Skeleton className="@max-[700px]:ml-auto h-3 w-14" />
    </div>

    <div className="@min-[700px]:px-7 flex items-center gap-2.5 border-t border-accent-border bg-accent-100 px-4 py-2.5">
      <Skeleton className="size-7 rounded-full" />
      <Skeleton className="h-4 w-36" />
      <Skeleton className="ml-auto h-3.5 w-20" />
    </div>

    {Array.from({ length: rowCount }, (_, index) => (
      <SummaryRowSkeleton key={index} />
    ))}
  </>
);

export { StepHeaderSkeleton, SummaryTableSkeleton };
